import {
  IPrintOrder,
  PrintOrderType,
} from "components/layoutsPrinter/IPrintOrder";
import { ILayoutPrinterHandler } from "components/layoutsPrinter/LayoutPrinter";
import { Kiosk } from "domains/kiosk/aggregates/Kiosk";
import { PrintType } from "domains/kiosk/aggregates/PrintType";
import { IPrintRepository } from "domains/order/interfaces/repositories/iprintRespository";
import { RefObject } from "react";
import { GetError } from "utils/GetError";
import { parsePrintRemote } from "./parsePrintRemote";
import { parsePrintResume } from "./parsePrintResume";
import { parsePrintTicket } from "./parsePrintTicket";
import { EletronicInvoiceTicket } from "domains/order/agreggates/EletronicInvoiceTicket/EletronicInvoiceTicket";
import { getGlobalAddLog } from "Infrastructure/repositories/appInsights/AppInsights";

const addLog = getGlobalAddLog();

export const localPrintUseCase = async (
  layoutPrintRef: RefObject<ILayoutPrinterHandler>,
  printRepository: IPrintRepository,
  printOrder: IPrintOrder,
  kiosk: Kiosk
) => {
  let printFile: string | null = "";

  try {
    printFile = await layoutPrintRef.current!.getPrintLayout(printOrder);
  } catch (error) {
    addLog && addLog("[ERROR] Printer", { printOrder, error });
    throw GetError(error, "localPrintUseCase", "LocalPrintUseCase");
  }

  addLog && addLog("Printer", { printOrder });

  switch (printOrder.type) {
    case PrintOrderType.ORDER:
      try {
        if (
          kiosk.oldPrinter ||
          kiosk.localId === "52d97f4e-5ad8-485f-9a18-85e622837359" // TODO: Remover id hardcoded
        ) {
          if (printOrder.printType === PrintType.TICKET) {
            // Old Printer
            // Ticket
            const printResult = await printRepository.printOrderAsync(
              parsePrintTicket(printOrder, kiosk?.kioskName ?? "Totem")
            );

            addLog && addLog("Printer", { printOrder, printResult });
            printFile = null;

            if (!printResult) {
              throw GetError(
                "Falha na impressão1",
                "localPrintUseCase",
                "LocalPrintUseCase"
              );
            }
          } else if (printOrder.printType === PrintType.RESUME) {
            // Old Printer
            // Resume
            const printResult = await printRepository.printResumeAsync(
              parsePrintResume(printOrder, kiosk, printFile)
            );

            addLog && addLog("Printer", { printOrder, printResult });
            printFile = null;

            if (!printResult) {
              throw GetError(
                "Falha na impressão2",
                "localPrintUseCase",
                "LocalPrintUseCase"
              );
            }
          }
        } else {
          // New Printer
          // Resume/Ticket
          const printResult = await printRepository.printImage(printFile);
          addLog && addLog("Printer", { printOrder, printResult });
          printFile = null;

          if (!printResult) {
            throw GetError(
              "Falha na impressão3",
              "localPrintUseCase",
              "LocalPrintUseCase"
            );
          }
        }
      } catch (error) {
        addLog && addLog("[ERROR] Printer", { printOrder, error });
        throw GetError(
          error,
          "localPrintUseCase",
          "LocalPrintUseCase"
        );
      }
      break;

    case PrintOrderType.REMOTE_PRINT:
      try {
        if (printOrder.addressLocalServer) {
          printRepository.remotePrintAsync(
            printOrder.addressLocalServer,
            parsePrintRemote(printOrder, kiosk, printFile)
          );
        } else {
          addLog && addLog("[ERROR] RemotePrinter", { printOrder, error: "Sem endereço de servidor" });
        }
      } catch (error) {
        addLog && addLog("[ERROR] RemotePrinter", { printOrder, error });
      }
      break;
    case PrintOrderType.CPF_REGISTER:
    case PrintOrderType.OFFLINE_RECHARGE:
    case PrintOrderType.WALLET_RECEIPT:
    case PrintOrderType.WALLET_RECHARGE:
      try {
        const printResult = await printRepository.printImage(printFile);
        addLog && addLog("Printer", { printOrder, printResult });
        printFile = null;

        if (!printResult) {
          throw GetError(
            "Falha na impressão",
            "localPrintUseCase",
            "LocalPrintUseCase"
          );
        }
      } catch (error) {
        addLog && addLog("[ERROR] Printer", { printOrder, error });
      }
      break;
    case PrintOrderType.NFCE:
      if (kiosk.newPrinter) {
        try {
          const printResult = await printRepository.printImage(printFile);
          addLog && addLog("Printer", { printOrder, printResult });
          printFile = null;
  
          if (!printResult) {
            throw GetError(
              "Falha na impressão",
              "localPrintUseCase",
              "LocalPrintUseCase"
            );
          }
        } catch (error) {
          addLog && addLog("[ERROR] Printer", { printOrder, error });
        }
      } else {
        try {
          const ticket: EletronicInvoiceTicket = {
            companyDocument: printOrder.data.cnpj,
            danfe: printOrder.data.danfe,
            emissionDate: printOrder.data.emissionDate,
            itens: printOrder.data.itens,
            key: printOrder.data.key,
            mustPrintCompleteDANFE: printOrder.data.mustPrintCompleteDANFE,
            numbering: printOrder.data.numbering,
            protocol: printOrder.data.protocol,
            razaosocial: printOrder.data.company,
            serie: printOrder.data.serie,
            status: printOrder.data.status,
            totalValue: printOrder.data.totalValue,
            client: printOrder.data?.client ?? '',
            header: printOrder.data.header,
            text: printOrder.data.text,
            infoInteresseContribuinte: printOrder.data.infoInteresseContribuinte,
            infoInteresseFisco: printOrder.data.infoInteresseFisco
          }
          const printResult = await printRepository.printEletronicInvoiceAsync(ticket);
          addLog && addLog("[Printer", { printOrder, ticket, printResult });
          if (printResult) {
            return { status: true, message: `NFCe ${ticket.key} impressa.` };
          } else {
            addLog && addLog("[ERROR] Printer", { printOrder, printResult, error: "Sem resultado de impressão" });
            return { status: false, message: `NFCe ${ticket.key} não impressa.` };
          }
        } catch (error) {
          addLog && addLog("[ERROR] Printer", { printOrder, error });
        }
      }
      break;
    case PrintOrderType.PINPAD_RECEIPT:
      try {
        const printResult = await printRepository.printStringAsync(
          printOrder.data.receipt
        );
        addLog && addLog("Printer", { printOrder, result: printResult });
        printFile = null;

        if (!printResult) {
          throw GetError(
            "Falha na impressão",
            "localPrintUseCase",
            "LocalPrintUseCase"
          );
        }
      } catch (error) {
        addLog && addLog("[ERROR] Printer", { printOrder, error });
      }
      break;
  }
};
